import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Formik } from "formik";
import { Button, ErrorMessage, LoadingIndicator, SelectableCards, Text } from "@jsq/ds/core";
import { useGetCountryCodesQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { CountrySelectionDropDown } from "../../../../common/components/CountrySelectionDropDown";
import { PaymentMethod } from "../../../../common/enums";
import { WorkflowMode } from "../../common/enums";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { useAvailablePaymentRails } from "../../hooks/useAvailablePaymentRails";
import { type ChildComponentProps } from "../types";
import * as styles from "./styles_vanilla.css";
import { OTHER_RAIL_CODE, RAIL_PRESENTATION, toRailCardOption } from "./types";

const COUNTRY_FORM_INITIAL_VALUES = { paymentPrefSelectBankCountry: "" };
const noop = () => undefined;

export const SelectPaymentMethodV2: React.FC<ChildComponentProps> = ({
  onBackButtonName = "Back",
  onContinueClick,
  onBackClick,
  onBeforeContinue,
}) => {
  const {
    paymentMethods,
    otherTileConfig,
    railsQueryFilters,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    setSelectedBankCountry,
    setBankCountryLocked,
    setAvailablePaymentRailsResponse,
    workflowMode,
  } = useConsolidatedPaymentPrefContext();

  // Country list is needed to resolve the full country (name/alpha2code) from the selected id
  // before persisting it to context for downstream rails (BACS/BECS/EFT/SEPA).
  const { data: countryData } = useGetCountryCodesQuery({ context: clientContext() });

  // LP/account workflows have no positionId — country must be selected upfront before querying rails.
  const isAccountOrLpWorkflow = !railsQueryFilters?.positionId;

  const [countryId, setCountryId] = useState<string | undefined>(undefined);
  const [selectedRail, setSelectedRail] = useState<string | undefined>(
    selectedPaymentMethod?.toLowerCase(),
  );
  const [showError, setShowError] = useState(false);
  const autoAdvancedRef = useRef(false);

  // Skip the initial API call for LP/account workflows until the user picks a country.
  const waitingForCountry = isAccountOrLpWorkflow && !countryId;

  const {
    response,
    loading,
    error: paymentRailsError,
    refetch,
  } = useAvailablePaymentRails({
    ...(railsQueryFilters ?? {}),
    countryId,
    skip: waitingForCountry,
  });

  useEffect(() => {
    if (response) {
      setAvailablePaymentRailsResponse(response);
    }
  }, [response, setAvailablePaymentRailsResponse]);

  const rails = useMemo(() => response?.rails ?? [], [response?.rails]);

  // Backend signals that a country is required before it can list rails (position-level flows).
  const needsCountryFirst = response?.requiresCountrySelection === true;

  // True when we are waiting for a country selection — either because the backend requires it
  // (position flow) or because the LP/account flow hasn't had a country picked yet.
  const showingCountryPrompt = waitingForCountry || needsCountryFirst;

  // Show the dropdown once a country is needed or has already been selected.
  const showCountryDropdown = showingCountryPrompt || countryId !== undefined;

  // "Other" is not a rail, so it never appears in `rails`. It is appended from the caller-supplied
  // `availablePaymentMethods` allowlist, which in practice means the subscription controller — the
  // only caller that puts `Other` in its allowlist. (The account container's `allowedMethodsOverride`
  // prop could technically carry `Other` too, but that is B1.2 scaffolding with no remaining
  // consumer, being removed in FF-8397.)
  //
  // Two gates, both load-bearing for the auto-advance effect below, which fires on
  // `cardOptions.length === 1`:
  //  - `!showingCountryPrompt`: Other sits behind bank-country selection per the mocks. It also
  //    keeps `cardOptions` empty during the prompt, so an allowlist of [Other, <one rail>] can't
  //    auto-advance straight past the country step into the Other form. Note this gate is the *only*
  //    thing covering the skipped-query window: while `waitingForCountry` holds the query never
  //    fires, so `railsSettled` below is vacuously true with no rails loaded.
  //  - `railsSettled`: while the query is in flight `rails` is empty, so a lone Other tile would
  //    otherwise look like the only option and auto-advance before the real rails arrive.
  const railsSettled = !loading && !paymentRailsError;
  // A backend rail literally coded "other" would collide with the appended tile — two options with
  // the same `value` means duplicate keys and an ambiguous selection. No rail uses that code today;
  // if one ever does, it wins and we skip the synthetic tile.
  const railsIncludeOtherCode = rails.some((rail) => rail.railCode === OTHER_RAIL_CODE);
  const showOtherTile =
    railsSettled &&
    !showingCountryPrompt &&
    !railsIncludeOtherCode &&
    paymentMethods?.[PaymentMethod.Other]?.isAvailable === true;

  const cardOptions = useMemo(() => {
    const options = rails.map(toRailCardOption);
    if (showOtherTile) {
      const presentation = RAIL_PRESENTATION[OTHER_RAIL_CODE];
      options.push({
        title: otherTileConfig?.label ?? presentation.title,
        details: presentation.details,
        icon: presentation.icon,
        value: OTHER_RAIL_CODE,
      });
    }
    return options;
  }, [otherTileConfig?.label, rails, showOtherTile]);

  const handleCountrySelect = useCallback(
    (value: string | number | string[] | number[]) => {
      const nextCountryId = String(value);
      setCountryId(nextCountryId);
      setSelectedRail(undefined);
      setShowError(false);
      autoAdvancedRef.current = false;
      setSelectedPaymentMethod(undefined);
      const matched = countryData?.countries?.edges?.find(
        (edge) => edge?.node?.id === nextCountryId,
      )?.node;
      if (matched) {
        setSelectedBankCountry({
          id: matched.id,
          name: matched.name,
          alpha2code: matched.alpha2code ?? null,
        });
        // Lock the country field on downstream forms — the user picked it here explicitly.
        setBankCountryLocked(true);
      } else {
        setSelectedBankCountry(undefined);
        setBankCountryLocked(false);
      }

      void refetch({ ...(railsQueryFilters ?? {}), countryId: nextCountryId });
    },
    [
      countryData,
      railsQueryFilters,
      refetch,
      setSelectedBankCountry,
      setBankCountryLocked,
      setSelectedPaymentMethod,
    ],
  );

  const advance = useCallback(
    (railCode: string) => {
      const method = RAIL_PRESENTATION[railCode]?.paymentMethod;
      if (!method) {
        setShowError(true);
        return;
      }
      setSelectedPaymentMethod(method);
      onBeforeContinue?.(method);
      // Only advance in Create mode — in Edit mode the form replaces this component at the same step index.
      if (workflowMode === WorkflowMode.Create) {
        onContinueClick();
      }
    },
    [onBeforeContinue, onContinueClick, setSelectedPaymentMethod, workflowMode],
  );

  // Auto-advance when exactly one option is selectable and no country is needed.
  // `autoAdvancedRef` ensures this runs at most once per query response.
  //
  // If `selectedPaymentMethod` is already set when this fires, the user previously auto-advanced
  // through this step and has since navigated back. In that case, exit the workflow via
  // `onBackClick` instead of bouncing forward again.
  useEffect(() => {
    if (autoAdvancedRef.current) return;
    if (response?.requiresCountrySelection) return;
    if (cardOptions.length !== 1) return;
    const singleOption = cardOptions[0];
    if (!RAIL_PRESENTATION[singleOption.value]?.paymentMethod) return;

    autoAdvancedRef.current = true;

    if (selectedPaymentMethod && railsQueryFilters?.positionId) {
      onBackClick();
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    advance(singleOption.value);
  }, [advance, cardOptions, onBackClick, response?.requiresCountrySelection]);

  // `selectedRail` is seeded from `selectedPaymentMethod` on mount, so it can name an option that
  // isn't actually on offer — e.g. editing an existing Other pref in a flow whose allowlist omits
  // Other. Nothing renders as selected in that case, so treat it as no selection rather than letting
  // `advance()` resolve the code and jump into a form the user was never shown.
  const hasOfferedSelection =
    selectedRail !== undefined && cardOptions.some((option) => option.value === selectedRail);

  const handleContinue = useCallback(() => {
    if (!hasOfferedSelection) {
      setShowError(true);
      return;
    }
    advance(selectedRail);
  }, [advance, hasOfferedSelection, selectedRail]);

  const renderTitle = () => (
    <Text className={styles.title} variant="h3">
      {workflowMode === WorkflowMode.Edit
        ? "Update payment instructions"
        : "Create new payment instructions"}
    </Text>
  );

  const renderMainContent = () => (
    <>
      {showCountryDropdown && (
        <div className={styles.countrySection}>
          <Formik initialValues={COUNTRY_FORM_INITIAL_VALUES} onSubmit={noop}>
            <CountrySelectionDropDown
              name="paymentPrefSelectBankCountry"
              label="Select the recipient's bank country"
              placeholder="Select a country"
              handleChange={handleCountrySelect}
              getValue={(country) => country.id}
            />
          </Formik>
        </div>
      )}
      <Text className={styles.methodSectionHeader} variant="h4">
        Select payment method
      </Text>
      {showingCountryPrompt && (
        <Text className={styles.emptyStateHelperText}>
          Payment method options will appear once the bank country is selected
        </Text>
      )}
      {!showingCountryPrompt && loading && <LoadingIndicator />}
      {!showingCountryPrompt && !loading && cardOptions.length > 0 && (
        <SelectableCards
          value={selectedRail}
          onSelectOption={(value) => setSelectedRail(value ? String(value) : undefined)}
          className={styles.options}
          id="paymentPrefSelectPaymentMethodV2"
          options={cardOptions}
        />
      )}
      {!showingCountryPrompt && !loading && !paymentRailsError && cardOptions.length === 0 && (
        <Text className={styles.emptyStateHelperText}>
          No payment methods are available for this selection.
        </Text>
      )}
    </>
  );

  const renderErrors = () => (
    <>
      {paymentRailsError && (
        <ErrorMessage>Failed to load payment methods. Please try again.</ErrorMessage>
      )}
      {showError && hasOfferedSelection && !RAIL_PRESENTATION[selectedRail]?.paymentMethod && (
        <ErrorMessage>
          Payment form for this method is coming soon. Please pick another option.
        </ErrorMessage>
      )}
      {/* Covers both "nothing picked" and a seeded selection that isn't among the offered tiles. */}
      {showError && !hasOfferedSelection && (
        <ErrorMessage>You must select a payment method to continue</ErrorMessage>
      )}
    </>
  );

  const renderFooter = () => (
    <div className={styles.footer}>
      <Button
        className={styles.backButton}
        variant="neutral"
        id="paymentPrefSelectPaymentsV2BackButton"
        onClick={onBackClick}
      >
        {onBackButtonName}
      </Button>
      <Button
        variant="primary"
        id="paymentPrefSelectPaymentsV2ContinueButton"
        onClick={handleContinue}
        disabled={showingCountryPrompt || loading || cardOptions.length === 0}
      >
        Continue
      </Button>
    </div>
  );

  return (
    <>
      {renderTitle()}
      {renderMainContent()}
      {renderErrors()}
      {renderFooter()}
    </>
  );
};
